import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n2 } from "../../lib/format";
import { Notice, Pager, Stat } from "../../lib/ui";

/*
 * Approved purchase orders – port of Approvedpo_list.php (API: GET /api/purchase-orders/approved, backend/admin.py)
 * Search by client or PO number, filter by PO date, 10 per page; totals reflect the filtered list.
 * "Details" shows the PO's items (get_po_details.php). "Create new PO" opens purchase_order.
 */

const PER_PAGE = 10;
const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB") : "N/A");

export default function ApprovedPOs() {
  const api = useApi();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  const [details, setDetails] = useState({}); // po_id -> data | {error}

  const toggle = (id) => {
    setOpen((o) => (o === id ? null : id));
    if (!details[id]) api.get(`/api/purchase-orders/${id}`).then((d) => setDetails((x) => ({ ...x, [id]: d })))
      .catch((e) => setDetails((x) => ({ ...x, [id]: { error: e.message } })));
  };

  useEffect(() => { api.get("/api/purchase-orders/approved").then(setRows).catch((e) => setError(e.message)); }, [api]);

  const filtered = useMemo(() => (rows || []).filter((po) => {
    const term = q.toLowerCase();
    const text = (po.client_name || "").toLowerCase().includes(term) || String(po.po_id).includes(term);
    return text && (!start || po.po_date >= start) && (!end || po.po_date <= end);
  }), [rows, q, start, end]);
  const pages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const cur = Math.min(page, pages);
  const total = filtered.reduce((s, po) => s + po.amount, 0);

  return (
    <main className="pg">
      <PageHeader slug="Approvedpo_list" actions={<Link to="/purchase_order" className="btn primary"><i className="fas fa-plus" /> Create new PO</Link>} />
      <Notice notice={error && { tone: "err", text: error }} />
      <div className="ui-stats">
        <Stat icon="fa-file-contract" label="Total orders" value={filtered.length} />
        <Stat icon="fa-coins" tone="up" label="Total amount" value={n2(total)} unit="TZS" />
      </div>
      <section className="panel">
        <div className="toolbar">
          <div className="field grow"><label htmlFor="po-q">Search</label>
            <input id="po-q" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Client or PO number" /></div>
          <div className="field"><label htmlFor="po-s">Start date</label><input id="po-s" type="date" value={start} onChange={(e) => { setStart(e.target.value); setPage(1); }} /></div>
          <div className="field"><label htmlFor="po-e">End date</label><input id="po-e" type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPage(1); }} /></div>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>PO #</th><th>Client</th><th>PO date</th><th>Approved by</th><th>Approval date</th><th className="num">Total amount</th><th /></tr></thead>
            <tbody>
              {filtered.slice((cur - 1) * PER_PAGE, cur * PER_PAGE).map((po) => {
                const d = details[po.po_id];
                return (
                  <Fragment key={po.po_id}>
                    <tr>
                      <td className="mono">#{po.po_id}</td><td style={{ fontWeight: 600, color: "var(--ink)" }}>{po.client_name}</td>
                      <td>{fmt(po.po_date)}</td><td>{po.approved_by || "N/A"}</td><td>{fmt(po.approval_date)}</td>
                      <td className="num mono">TZS {n2(po.amount)}</td>
                      <td style={{ whiteSpace: "nowrap" }}><button type="button" className="btn sm" aria-expanded={open === po.po_id} onClick={() => toggle(po.po_id)}>
                        {open === po.po_id ? "Hide" : "Details"}</button> <Link to={`/po/${po.po_id}`} className="btn sm">Open</Link></td>
                    </tr>
                    {open === po.po_id && (
                      <tr className="po-detail"><td colSpan={7}><div className="inner">
                        {!d && <span style={{ color: "var(--muted)" }}><i className="fas fa-spinner fa-spin" /> Loading…</span>}
                        {d?.error && <span style={{ color: "var(--danger)" }}>Couldn't load details: {d.error}</span>}
                        {d && !d.error && (<>
                          <dl><div><dt>Email</dt><dd>{d.client.contact_email || "–"}</dd></div><div><dt>Phone</dt><dd>{d.client.contact_phone || "–"}</dd></div>
                            <div><dt>Status</dt><dd>{d.status}</dd></div></dl>
                          <table><thead><tr><th>#</th><th>Product</th><th className="num">Qty</th><th className="num">Unit price</th><th className="num">Total</th></tr></thead>
                            <tbody>{d.items.map((it, i) => (
                              <tr key={it.item_id}><td className="c">{i + 1}</td><td className="c">{it.description}</td><td className="c num mono">{it.quantity}</td>
                                <td className="c num mono">{n2(it.unit_price)}</td><td className="c num mono">{n2(it.total)}</td></tr>))}</tbody>
                            <tfoot><tr><td className="c" colSpan={4}><b>Total</b></td><td className="c num mono"><b>{n2(d.grand_total)}</b></td></tr></tfoot>
                          </table>
                          {d.items.length === 0 && <span style={{ color: "var(--muted)" }}>No items on this order.</span>}
                        </>)}
                      </div></td></tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {rows && filtered.length === 0 && <div className="empty"><i className="fas fa-file-circle-question" />No purchase orders found. Try adjusting your filters.</div>}
          {!rows && !error && <div className="empty"><i className="fas fa-spinner fa-spin" />Loading…</div>}
        </div>
        <Pager page={cur} totalPages={pages} onPage={setPage} />
      </section>
    </main>
  );
}
